<script>
import { mapGetters } from 'vuex';
import { ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import { clearCookiesOnLogout } from 'dashboard/store/utils/api.js';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { parseBoolean } from '@chatwoot/utils';
import NextButton from 'dashboard/components-next/button/Button.vue';
import UserProfilePicture from './UserProfilePicture.vue';
import UserBasicDetails from './UserBasicDetails.vue';
import MessageSignature from './MessageSignature.vue';
import UserLanguageSelect from './UserLanguageSelect.vue';
import Appearance from './Appearance.vue';
import ChangePassword from './ChangePassword.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import MfaSettingsCard from './MfaSettingsCard.vue';

export default {
  components: {
    MessageSignature,
    SectionLayout,
    UserLanguageSelect,
    Appearance,
    UserProfilePicture,
    UserBasicDetails,
    ChangePassword,
    MfaSettingsCard,
    BaseSettingsHeader,
    NextButton,
  },
  setup() {
    const changePasswordDialogRef = ref(null);

    const openChangePasswordDialog = () =>
      changePasswordDialogRef.value?.open();

    return {
      changePasswordDialogRef,
      openChangePasswordDialog,
    };
  },
  data() {
    return {
      avatarUrl: '',
      name: '',
      displayName: '',
      email: '',
      messageSignature: '',
    };
  },
  computed: {
    ...mapGetters({
      currentUser: 'getCurrentUser',
      currentUserId: 'getCurrentUserID',
      globalConfig: 'globalConfig/get',
    }),
    isMfaEnabled() {
      return parseBoolean(window.chatwootConfig?.isMfaEnabled);
    },
  },
  mounted() {
    if (this.currentUserId) {
      this.initializeUser();
    }
  },
  methods: {
    initializeUser() {
      this.name = this.currentUser.name;
      this.email = this.currentUser.email;
      this.avatarUrl = this.currentUser.avatar_url;
      this.displayName = this.currentUser.display_name;
      this.messageSignature = this.currentUser.message_signature;
    },
    async dispatchUpdate(payload, successMessage, errorMessage) {
      let alertMessage = '';
      try {
        await this.$store.dispatch('updateProfile', payload);
        alertMessage = successMessage;

        return true; // return the value so that the status can be known
      } catch (error) {
        alertMessage = parseAPIErrorResponse(error) || errorMessage;

        return false; // return the value so that the status can be known
      } finally {
        useAlert(alertMessage);
      }
    },
    async updateProfile(userAttributes) {
      const { name, email, displayName } = userAttributes;
      const nextEmail = email ?? this.email;
      const hasEmailChanged = this.currentUser.email !== nextEmail;
      this.name = name || this.name;
      this.email = nextEmail;
      this.displayName = displayName || this.displayName;

      const updatePayload = {
        name: this.name,
        email: this.email,
        displayName: this.displayName,
      };

      const success = await this.dispatchUpdate(
        updatePayload,
        hasEmailChanged
          ? this.$t('PROFILE_SETTINGS.AFTER_EMAIL_CHANGED')
          : this.$t('PROFILE_SETTINGS.UPDATE_SUCCESS'),
        this.$t('RESET_PASSWORD.API.ERROR_MESSAGE')
      );

      if (hasEmailChanged && success) clearCookiesOnLogout();
    },
    async updateSignature(signature) {
      const payload = { message_signature: signature };
      let successMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_SUCCESS'
      );
      let errorMessage = this.$t(
        'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.API_ERROR'
      );

      await this.dispatchUpdate(payload, successMessage, errorMessage);
    },
    async updateProfilePicture({ file, url }) {
      const previousAvatarUrl =
        this.avatarUrl || this.currentUser.avatar_url || '';
      this.avatarUrl = url;

      const success = await this.dispatchUpdate(
        { avatar: file },
        this.$t('PROFILE_SETTINGS.UPDATE_SUCCESS'),
        this.$t('PROFILE_SETTINGS.AVATAR_UPDATE_FAILED')
      );
      this.avatarUrl = success
        ? this.currentUser.avatar_url || previousAvatarUrl
        : previousAvatarUrl;
    },
    async deleteProfilePicture() {
      try {
        await this.$store.dispatch('deleteAvatar');
        this.avatarUrl = '';
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('PROFILE_SETTINGS.AVATAR_DELETE_FAILED'));
      }
    },
  },
};
</script>

<template>
  <div class="flex w-full min-w-0 flex-col">
    <BaseSettingsHeader
      :title="$t('PROFILE_SETTINGS.TITLE')"
      :description="$t('PROFILE_SETTINGS.DESCRIPTION')"
    />
    <div class="w-full px-8 md:px-10">
      <div class="max-w-4xl">
        <SectionLayout title="" description="" class="!pt-0">
          <div class="flex flex-col gap-6 sm:flex-row sm:items-start">
            <div class="shrink-0 sm:w-56 flex flex-col gap-3">
              <UserProfilePicture
                :src="avatarUrl"
                :name="name"
                @change="updateProfilePicture"
                @delete="deleteProfilePicture"
              />
            </div>
            <div class="flex-1 min-w-0 w-full">
              <UserBasicDetails
                :name="name"
                :display-name="displayName"
                :email="email"
                @update-user="updateProfile"
              >
                <template #extra-fields>
                  <UserLanguageSelect
                    :label="
                      $t(
                        'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.TITLE'
                      )
                    "
                  />
                </template>
                <template
                  v-if="!globalConfig.disableUserProfileUpdate"
                  #actions
                >
                  <NextButton
                    type="button"
                    variant="faded"
                    color="slate"
                    :label="
                      $t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.BTN_TEXT')
                    "
                    @click="openChangePasswordDialog"
                  />
                </template>
              </UserBasicDetails>
            </div>
          </div>
        </SectionLayout>
      </div>
      <div
        class="w-full shrink-0 border-0 border-t border-solid border-n-weak"
        role="presentation"
      />
      <div class="max-w-4xl">
        <SectionLayout title="" description="">
          <Appearance
            :label="
              $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.APPEARANCE.TITLE')
            "
            :description="
              $t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.APPEARANCE.NOTE')
            "
          />
        </SectionLayout>
      </div>
      <div
        class="w-full shrink-0 border-0 border-t border-solid border-n-weak"
        role="presentation"
      />
      <div class="max-w-4xl">
        <SectionLayout
          :title="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.TITLE')"
          :description="
            $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.NOTE')
          "
        >
          <MessageSignature
            :message-signature="messageSignature"
            @update-signature="updateSignature"
          />
        </SectionLayout>
      </div>
      <div
        v-if="isMfaEnabled"
        class="w-full shrink-0 border-0 border-t border-solid border-n-weak"
        role="presentation"
      />
      <template v-if="isMfaEnabled">
        <div class="max-w-4xl">
          <SectionLayout
            :title="$t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.TITLE')"
            :description="$t('PROFILE_SETTINGS.FORM.SECURITY_SECTION.NOTE')"
          >
            <MfaSettingsCard />
          </SectionLayout>
        </div>
      </template>
    </div>
    <ChangePassword
      v-if="!globalConfig.disableUserProfileUpdate"
      ref="changePasswordDialogRef"
    />
  </div>
</template>
